import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { cn } from "@/core/utils/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground shadow",
          "hover:bg-[#009973]",
          "active:bg-[#008f6b]",
          "disabled:bg-bg-disabled disabled:text-tertiary-text",
        ],
        destructive: [
          "bg-destructive text-destructive-foreground shadow-sm",
          "hover:bg-[#fc1303]",
          "active:bg-[#e60f00]",
        ],
        tertiary: [
          "border border-stroke-secondary bg-surface-primary text-secondary-text shadow-sm",
          "hover:border-stroke-primary hover:text-primary-text",
          "active:bg-surface-secondary active:text-primary-text",
          "disabled:bg-surface-secondary disabled:text-stroke-secondary disabled:border-stroke-tertiary",
        ],
        secondary: [
          "bg-surface-primary text-interactive-text border-2 border-stroke-interactive shadow-sm",
          "hover:text-[#029772] hover:border-[#029772]",
          "active:text-[#038c6c] active:border-[#038c6c]",
          "disabled:bg-surface-secondary disabled:text-stroke-secondary disabled:border-stroke-tertiary",
        ],
        ghost: [
          "bg-transparent",
          "hover:bg-[#B4E9DC] hover:text-primary-text",
          "active:bg-[#B4E9DC]",
        ],
        text: [
          "text-secondary-text",
          "hover:text-primary-text",
          "active:text-primary-text",
          "disabled:text-stroke-secondary",
        ],
        link: [
          "text-primary underline-offset-4",
          "hover:underline",
          "active:text-primary/70",
        ],
        icon: [
          "bg-surface-primary text-primary-text",
          "active:bg-surface-secondary",
          "disabled:bg-surface-secondary",
        ],
        destructiveSecondary: [
          "bg-surface-primary text-[#e60f00] border-2 border-[#e60f00] shadow-sm",
          "hover:text-[#fc1303] hover:border-[#fc1303]",
          "disabled:bg-surface-secondary disabled:text-stroke-secondary disabled:border-stroke-tertiary",
        ],
        unstyled: ["cursor-pointer"],
      },
      size: {
        default: "h-12 px-6 py-3 rounded-full text-lg font-semibold",
        sm: "h-10 px-6 py-3 rounded-full text-lg font-semibold",
        xs: "h-8 px-3 py-1.5 rounded-md text-xs font-medium",
        // The compact pill: the rounded language of `default`/`sm`/`lg` at a
        // size that fits a dense list row. `xs` is the only other small size
        // and it is square-cornered, so any compact UI was forced off the
        // pill. Sized on the type scale (`sm` = 11px), unlike `xs`.
        xsPill: "h-8 px-4 py-1.5 rounded-full text-sm font-semibold",
        lg: "h-14 rounded-full px-6 py-3 text-lg font-semibold",
        icon: "h-10 w-10 min-w-10 rounded-full",
        iconSm: "h-6 w-6 min-w-6 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
}

const getSpinnerColor = (variant: ButtonProps["variant"]) => {
  const colorMap = {
    default: "inverse",
    destructive: "inverse",
    secondary: "interactive",
    destructiveSecondary: "interactive",
    tertiary: "secondary",
    ghost: "primary",
    icon: "primary",
    text: "secondary",
    link: "primary",
    unstyled: "default",
  } as const;

  return colorMap[variant || "default"];
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      loading = false,
      disabled,
      "aria-busy": ariaBusy,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const fullWidthClass = fullWidth ? "w-full" : "";
    const isDisabled = disabled || loading;
    const ariaBusyState = ariaBusy ?? (loading || undefined);
    const spinnerColor = getSpinnerColor(variant);

    if (variant === "unstyled") {
      return (
        <Comp
          className={cn("cursor-pointer", className, fullWidthClass)}
          ref={ref}
          disabled={isDisabled}
          aria-busy={ariaBusyState}
          {...props}
        >
          <>
            {loading ? (
              <LoadingSpinner
                size="sm"
                color={spinnerColor}
                containerClassName="!h-auto !w-auto"
              />
            ) : (
              leftIcon
            )}
            {children}
            {rightIcon}
          </>
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          fullWidthClass,
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={ariaBusyState}
        {...props}
      >
        <>
          {loading ? (
            <LoadingSpinner
              size="sm"
              color={spinnerColor}
              containerClassName="!h-auto !w-auto"
            />
          ) : (
            leftIcon
          )}
          {children}
          {rightIcon}
        </>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
